import { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";

import { useConsumerDeals } from "../../CustomHooks/Queries";
import { useCreatePublicGridEmbed } from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";

import { dispatchAPIErrorToast } from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";

import { StakeLogo } from "../../Assets/Images";
import CloseButton from "../../Components/Buttons/close-button";
import PrimaryButton from "../../Components/Buttons/primary-button";
import CheckBox from "../../Components/Form Fields/checkbox";

import "./publicgrid.scss";

export default function ApplyPublicGrid() {
  const queryClient = useQueryClient();
  const { onModalClose } = useModalContext();
  const { data: consumerDealsData } = useConsumerDeals();

  const publicGridOffer = consumerDealsData?.data?.data?.offers?.find(
    (offer) => offer.id === "stake_partner_publicgrid",
  );
  const skipConsent = Boolean(publicGridOffer?.is_activated || publicGridOffer?.is_consented);

  const [acknowledged, setAcknowledged] = useState(false);
  const [hasConsented, setHasConsented] = useState(skipConsent);
  const [embedUrl, setEmbedUrl] = useState(null);
  const embedRequested = useRef(false);

  useEffect(() => {
    if (skipConsent) {
      setHasConsented(true);
    }
  }, [skipConsent]);

  const {
    mutate: createEmbed,
    isError: isEmbedError,
    reset: resetEmbed,
  } = useCreatePublicGridEmbed({
    onSuccess: (response) => {
      setEmbedUrl(response?.data?.data?.url ?? null);
      queryClient.invalidateQueries(APIQueryKeys.consumerDeals);
      queryClient.invalidateQueries(APIQueryKeys.publicgridStatus);
    },
    onError: (error) =>
      dispatchAPIErrorToast({
        message: error?.response?.data?.error || "Failed to open Public Grid",
        key: "publicgridEmbed",
      }),
  });

  useEffect(() => {
    if (!hasConsented || embedRequested.current) return;
    embedRequested.current = true;
    createEmbed({ flow_type: "move-in" });
  }, [hasConsented, createEmbed]);

  const handleRetry = () => {
    resetEmbed();
    setEmbedUrl(null);
    embedRequested.current = true;
    createEmbed({ flow_type: "move-in" });
  };

  const isLoadingEmbed = hasConsented && !isEmbedError && !embedUrl;

  return (
    <div className="publicgrid-container">
      <div className="publicgrid-header">
        <StakeLogo className="stake-logo" />
        <CloseButton onClick={onModalClose} className="close" />
      </div>

      {!hasConsented ? (
        <div className="publicgrid-consent-gate">
          <div className="d-flex align-items-start gap-2 mb-4">
            <CheckBox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              ariaLabelledBy="publicgrid-consent-label"
            />
            <p className="text-base-regular color--charcoal mb-0 px-2" id="publicgrid-consent-label">
              By selecting the checkbox, you consent to sharing your name, email, phone, and service
              address with Public Grid to set up or manage your utility account.
            </p>
          </div>
          <PrimaryButton disabled={!acknowledged} onClick={() => setHasConsented(true)}>
            Continue
          </PrimaryButton>
        </div>
      ) : isLoadingEmbed ? (
        <div className="loading-content">
          <CircularProgress />
        </div>
      ) : isEmbedError || !embedUrl ? (
        <div className="publicgrid-consent-gate">
          <p className="text-base-regular color--charcoal mb-4">
            We couldn’t open Public Grid right now. Please try again.
          </p>
          <PrimaryButton onClick={handleRetry}>Try again</PrimaryButton>
        </div>
      ) : (
        <iframe
          src={embedUrl}
          title="Public Grid"
          className="publicgrid-iframe"
          allow="clipboard-write"
        />
      )}
    </div>
  );
}
